import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plane, Menu, X, User, LogOut } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthModal from '@/components/AuthModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Header({ onSearch, searchValue }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchValue || '');
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(localSearchQuery);
    }
    if (location.pathname !== '/marketplace') {
      navigate('/marketplace');
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleAccountClick = () => {
    if (user?.user_type === 'seller') {
      navigate('/account/seller');
    } else {
      navigate('/account/buyer');
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer flex-shrink-0"
              onClick={() => navigate('/')}
              data-testid="logo-home-link"
            >
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Plane className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-lg font-bold hidden sm:inline">
                <span className="text-foreground">UAVs For Sale</span>
              </span>
            </div>

            {/* Search Bar - Desktop */}
            <form 
              onSubmit={handleSearch}
              className="hidden md:flex flex-1 max-w-xl mx-4"
            >
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by drone model..."
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  className="pl-10 pr-4 w-full bg-muted/50 border-border focus:bg-background"
                  data-testid="header-search-input"
                />
              </div>
            </form>

            {/* Navigation - Desktop */}
            <nav className="hidden lg:flex items-center gap-6">
              <button 
                onClick={() => navigate('/marketplace')}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="nav-categories"
              >
                Categories
              </button>
              <button 
                onClick={() => navigate('/about')}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="nav-about"
              >
                About
              </button>
            </nav>

            {/* Action Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <Button 
                onClick={() => navigate('/sell')}
                variant="outline"
                className="font-semibold"
                data-testid="sell-drone-btn"
              >
                Sell Your Drone
              </Button>
              
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2"
                      data-testid="user-menu-trigger"
                    >
                      <User className="w-4 h-4" />
                      {user?.name?.split(' ')[0] || 'Account'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem 
                      onClick={handleAccountClick}
                      className="cursor-pointer"
                      data-testid="my-account-link"
                    >
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleSignOut}
                      className="cursor-pointer text-red-600"
                      data-testid="sign-out-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={() => setAuthModalOpen(true)}
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                  data-testid="sign-in-btn"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Mobile Search Bar */}
          <form 
            onSubmit={handleSearch}
            className="md:hidden pb-3"
          >
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by drone model..."
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                className="pl-10 pr-4 w-full bg-muted/50 border-border"
                data-testid="mobile-search-input"
              />
            </div>
          </form>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-border py-4 animate-slide-up">
              <nav className="flex flex-col gap-4">
                <button 
                  onClick={() => {
                    navigate('/marketplace');
                    setMobileMenuOpen(false);
                  }}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors text-left"
                >
                  Categories
                </button>
                <button 
                  onClick={() => {
                    navigate('/about');
                    setMobileMenuOpen(false);
                  }}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors text-left"
                >
                  About
                </button>
                <Button 
                  onClick={() => {
                    navigate('/sell');
                    setMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="w-full font-semibold"
                >
                  Sell Your Drone
                </Button>
                
                {isAuthenticated ? (
                  <>
                    <Button 
                      onClick={handleAccountClick}
                      className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full"
                    >
                      <User className="w-4 h-4 mr-2" />
                      My Account
                    </Button>
                    <Button 
                      onClick={handleSignOut}
                      variant="outline"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={() => {
                      setAuthModalOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full"
                  >
                    Sign In
                  </Button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </>
  );
}
